// CONVERT v2 — the chooser's lists (UI rebuild batch 3; Astra's convert). The rows and their rules are screens/Swap.tsx's:
// parties whose box is not square (own accounts never), desk accounts holding something — or an exchange the venue holds
// a coin on, or a wallet the chain shows money on, at 0 on the books with that reading under the name — grouped by kind,
// two accounts under one name told apart by the asset. Rows sit in the page (no box of their own that scrolls: Astra's
// phone finding); the first ten show, the rest one tap away.
import { useState, type ReactNode } from 'react';
import type { PartySummary, TreasuryRow } from '../../../lib/api';
import { partyName, partyBoxWords, railName, railKindGroupLabel, foldAsset, dustFor } from '../../../lib/naming';
import { displayAsset, fmtAmount, fmtQuantity } from '../../../lib/format';

/** How many rows a list shows before "Show all". */
const FIRST = 10;

/** An exchange account whose venue holds more than dust of the asset (Swap.tsx). */
export const venueHolds = (r: TreasuryRow): boolean =>
  r.treasuryKind === 'exchange' && typeof r.venue_balance === 'number' && r.venue_balance > dustFor(r.asset);
/** A wallet the chain shows money on that the books hold nothing of (Swap.tsx). */
export const chainHolds = (r: TreasuryRow): boolean => r.outside?.kind === 'chain';
/** "1,56,742 USDC on chain, not yet booked" — the BFF's words for where the money is (Swap.tsx). */
export const chainWords = (r: TreasuryRow): string =>
  `${fmtQuantity(r.outside?.amount, r.asset)} ${displayAsset(r.asset)} ${r.outside?.words ?? ''}`;

function Row({ onClick, title, sub, figure }: { onClick: () => void; title: ReactNode; sub?: ReactNode; figure?: ReactNode }) {
  return (
    <li className="border-b border-stroke-soft last:border-b-0">
      <button type="button" onClick={onClick} className="flex min-h-[56px] w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-panel-2 active:bg-panel-2 lg:px-5">
        <span className="min-w-0">
          <span className="block truncate text-body font-medium">{title}</span>
          {sub && <span className="mt-0.5 block truncate text-meta text-tg-hint">{sub}</span>}
        </span>
        {figure && <span className="shrink-0 text-right text-body">{figure}</span>}
      </button>
    </li>
  );
}

function More({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="btn-link min-h-[44px] w-full px-4 text-left text-body lg:px-5">
      {count} more →
    </button>
  );
}

export function PartyRows({ rows, onPick }: { rows: PartySummary[]; onPick: (p: PartySummary) => void }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, FIRST);
  return (
    <>
      <ul>
        {shown.map((p) => (
          <Row key={p.slug} onClick={() => onPick(p)} title={<span title={partyName(p)}>{partyName(p)}</span>} sub={partyBoxWords(p)} />
        ))}
      </ul>
      {!all && rows.length > FIRST && <More count={rows.length - FIRST} onClick={() => setAll(true)} />}
    </>
  );
}

export function RailRows({ groups, sharedName, onPick }: {
  groups: { kind: string; rows: TreasuryRow[] }[];
  sharedName: (r: TreasuryRow) => boolean;
  onPick: (r: TreasuryRow) => void;
}) {
  const [all, setAll] = useState(false);
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  let left = all ? Infinity : FIRST;
  return (
    <>
      {groups.map((g) => {
        const rows = g.rows.slice(0, Math.max(0, left));
        left -= rows.length;
        if (rows.length === 0) return null;
        return (
          <div key={g.kind}>
            <p className="border-b border-stroke-soft bg-panel-2 px-4 py-2 text-label font-medium uppercase tracking-wider text-tg-hint lg:px-5">{railKindGroupLabel(g.kind)}</p>
            <ul>
              {rows.map((r) => (
                <Row
                  key={`${r.slug}-${r.asset}`}
                  onClick={() => onPick(r)}
                  title={sharedName(r) ? `${railName(r)} · ${foldAsset(r.asset)}` : railName(r)}
                  // A zero row's venue or chain reading is the line under its name: why it is listed.
                  sub={r.balance === 0 && venueHolds(r) ? `venue ${fmtQuantity(r.venue_balance, r.asset)}`
                    : r.balance === 0 && chainHolds(r) ? chainWords(r)
                    : undefined}
                  figure={<><span className="font-mono tabular">{fmtAmount(r.balance, r.asset)}</span> <span className="text-meta text-tg-hint">{displayAsset(r.asset)}</span></>}
                />
              ))}
            </ul>
          </div>
        );
      })}
      {!all && total > FIRST && <More count={total - FIRST} onClick={() => setAll(true)} />}
    </>
  );
}
